import { useMemo } from "react";
import { useSelector } from "react-redux";
import { completedIcon, DownloadIcon, uncompletedIcon } from "../../images";
import { Link } from "react-router-dom";
import { downloadPDF, renderValidUrl } from "../../utils/constants";
import { File, Lock } from "lucide-react";
import { formatDeadline } from "../../utils/time";

const AccordianCard = ({ item, tab, type = null, isEnrolled, course }) => {
  const { user } = useSelector((state) => state);

  const enrolled = useMemo(() => {
    if (typeof isEnrolled === "boolean") return isEnrolled;
    if (!course?.id) return false;
    const courses = user?.enrolledCourses || user?.enrolled_courses || [];
    return courses.some((c) => {
      const cid = typeof c === "object" ? c.id || c.course_id : c;
      return Number(cid) === Number(course.id);
    });
  }, [isEnrolled, user, course?.id]);

  const completed = item.completed === true || item.isCompleted === true;

  // Determine actual click target based on tab context
  const videoLink = tab === "curriculum" ? item.videoURL || "" : "";
  const videoName = tab === "curriculum" ? item.videoName || "" : "";

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!enrolled) return;
    if (!item.document) return;

    let url = "";
    let doc = item.document;
    if (Array.isArray(doc) && doc.length > 0) {
      doc = doc[0];
    }

    if (typeof doc === "string") {
      url = doc;
    } else if (doc.url) {
      url = doc.url;
    } else if (doc.data?.attributes?.url) {
      url = doc.data.attributes.url;
    } else if (doc.attributes?.url) {
      url = doc.attributes.url;
    }

    if (url) {
      const validUrl = renderValidUrl(url);
      downloadPDF(validUrl, item.name || "document");
    }
  };

  return (
    <div
      className={`bg-[#F4F4F4] p-4 rounded-[4px] flex justify-between h-[56px] ${
        enrolled && tab === "curriculum" ? "cursor-pointer" : ""
      }`}
    >
      {tab === "curriculum" &&
        (enrolled ? (
          <Link
            to={`/watch-video/${item.id}`}
            state={{ videoLink, videoName }}
            className="flex-1 flex items-center justify-between"
          >
            <div className="flex gap-4 items-center text-[14px] leading-[14px] font-medium text-[#1E1B2E]">
              <img
                src={completed ? completedIcon : uncompletedIcon}
                alt="status"
                className="w-[17px] h-[19px]"
              />
              <p>{item.videoName}</p>
            </div>
          </Link>
        ) : (
          <div className="flex-1 flex items-center justify-between select-none">
            <div className="flex gap-4 items-center text-[14px] leading-[14px] font-medium text-[#828282]">
              <img
                src={uncompletedIcon}
                alt="status"
                className="w-[17px] h-[19px] opacity-60"
              />
              <p>{item.videoName}</p>
            </div>
            <div
              className="flex items-center gap-1.5 text-[#828282] text-[12px]"
              title="Enroll to watch video"
            >
              <Lock className="w-4 h-4 text-[#828282]" />
              <span className="hidden sm:inline">Locked</span>
            </div>
          </div>
        ))}

      {tab === "deliverables" && (
        <div className="flex w-full justify-between items-center">
          <div className="flex flex-col gap-2 text-[14px] leading-[14px] font-medium">
            <p className="text-[14px] font-semibold">{item.name}</p>
            <p className="text-[12px] text-[#626262]">
              Deadline: {formatDeadline(item.deadline)}
            </p>
          </div>
        </div>
      )}

      {tab !== "curriculum" && tab !== "deliverables" && (
        <div className="flex w-full justify-between items-center">
          <div
            className={`flex gap-4 items-center text-[14px] leading-[14px] font-medium ${
              enrolled ? "text-[#1E1B2E]" : "text-[#828282]"
            }`}
          >
            {type !== "project" ? (
              <img
                src={uncompletedIcon}
                alt="icon"
                className={`w-[17px] h-[19px] ${!enrolled ? "opacity-60" : ""}`}
              />
            ) : (
              <File
                className={`w-6 h-6 ${!enrolled ? "text-[#828282]" : ""}`}
              />
            )}
            <p>{item.name}</p>
          </div>

          {enrolled ? (
            <div
              className="flex gap-[4px] items-center text-[14px] leading-[24px] text-[#333333] hover:text-[#000000] cursor-pointer transition-colors"
              onClick={handleDownload}
            >
              <img src={DownloadIcon} alt="download icon" className="w-6 h-6" />
              <p>Download</p>
            </div>
          ) : (
            <div
              className="flex gap-1.5 items-center text-[12px] sm:text-[14px] leading-[24px] text-[#828282] select-none"
              title="Enroll to download resources"
            >
              <Lock className="w-4 h-4 text-[#828282]" />
              <span className="hidden sm:inline">Locked</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccordianCard;
